import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Check, Sparkles } from 'lucide-react';
import { displayFont, fadeInUp, staggerContainer } from './shared';
import { SmallSelect } from '../inputs/SmallSelect';
import { CURRENCIES, CURRENCY_SELECT_OPTIONS } from '../../utils/currencies';
import type { HomepageStrings } from '../../i18n/homepage';

type PricingStrings = Pick<
  HomepageStrings,
  | 'pricing_badge'
  | 'pricing_h2_pre'
  | 'pricing_h2_highlight'
  | 'pricing_body_wedding'
  | 'pricing_body_anniversary'
  | 'pricing_wedding_features'
  | 'pricing_anniversary_features'
  | 'pricing_anniversary_not_included'
  | 'pricing_anniversary_wedding_link'
  | 'pricing_cta'
  | 'pricing_planner_heading'
  | 'pricing_planner_subheading'
  | 'pricing_planner_features'
  | 'pricing_planner_cta'
  | 'pricing_bespoke_pre'
  | 'pricing_bespoke_link'
  | 'pricing_most_popular'
  | 'pricing_one_off'
  | 'pricing_one_time_payment'
>;

const EN_DEFAULTS: PricingStrings = {
  pricing_badge: 'Pricing',
  pricing_h2_pre: 'Simple pricing, ',
  pricing_h2_highlight: 'no hidden fees',
  pricing_body_wedding: "One simple price per invite. Save the Date, day & night guests — all included. Design for free, pay when you're ready.",
  pricing_body_anniversary: "One price covers everything. Online RSVP, unlimited guests, all designs included. Design for free, pay when you're ready.",
  pricing_wedding_features: [
    'Save the Date included',
    'Unlimited guests',
    'Fully customisable',
    '5 stunning templates',
    'RSVP tracking & reminders',
    "See who's opened their invite",
    'Dietary requirements',
    'Plus-one controls',
    'Organise guests with tags',
    'Private & public invites',
    'Send via email or public link',
    'QR code available',
  ],
  pricing_anniversary_features: [
    'Online RSVP',
    'Unlimited guests',
    'Fully customisable',
    '5 stunning templates',
    'RSVP tracking & reminders',
    "See who's opened their invite",
    'Dietary requirements',
    'Plus-one controls',
    'Organise guests with tags',
    'Private & public invites',
    'Send via email or public link',
    'QR code available',
  ],
  pricing_anniversary_not_included: [
    'Save the Date',
    'Day & evening guest split',
    'Multiple venues',
  ],
  pricing_anniversary_wedding_link: 'See our wedding invitations from £39',
  pricing_cta: 'Create Your Invite',
  pricing_planner_heading: 'Planner',
  pricing_planner_subheading: 'For professional event planners',
  pricing_planner_features: [
    'Everything in the standard plan',
    'Up to 10 live invites at once',
    'Build unlimited draft invites',
    'Import guest lists via CSV',
    'Export guests, meal choices & dietary info to CSV',
    'Invite couples to manage their own invite',
    'Priority support',
  ],
  pricing_planner_cta: 'Get Started',
  pricing_bespoke_pre: 'Looking for something custom?',
  pricing_bespoke_link: 'Contact us about a bespoke design',
  pricing_most_popular: 'Most Popular',
  pricing_one_off: 'One-off',
  pricing_one_time_payment: 'One-time payment',
};

const EUR_LOCALES = ['fr', 'de', 'es', 'it', 'nl', 'pt', 'pl', 'sv', 'no', 'da', 'el'];

function detectCurrencyCode(locale?: string): string {
  if (locale && EUR_LOCALES.includes(locale)) return 'EUR';
  if (typeof navigator === 'undefined') return 'GBP';
  const lang = (navigator.language || (navigator.languages && navigator.languages[0]) || '').toLowerCase();
  if (lang === 'en-us' || lang.startsWith('en-us-')) return 'USD';
  const eurPrefixes = ['de', 'fr', 'es', 'it', 'nl', 'pt', 'pl', 'sv', 'no', 'nb', 'da', 'el', 'fi', 'cs', 'hu', 'ro'];
  if (eurPrefixes.some(p => lang.startsWith(p))) return 'EUR';
  return 'GBP';
}

export function PricingSection({
  eventContext = 'wedding',
  strings,
  locale,
}: {
  eventContext?: 'wedding' | 'anniversary';
  strings?: PricingStrings;
  locale?: string;
}) {
  const s = strings ?? EN_DEFAULTS;
  const [currencyCode, setCurrencyCode] = useState(() => detectCurrencyCode(locale));
  const [plan, setPlan] = useState<'oneoff' | 'planner'>('oneoff');

  useEffect(() => {
    setCurrencyCode(detectCurrencyCode(locale));
  }, [locale]);

  const currency = CURRENCIES.find(c => c.code === currencyCode) ?? CURRENCIES[0];
  const isAnniversary = eventContext === 'anniversary';
  const perInvitePrice = isAnniversary ? currency.perAnniversary : currency.perInvite;
  const gbpBasePrice = isAnniversary ? 19 : 39;
  const features = isAnniversary ? s.pricing_anniversary_features : s.pricing_wedding_features;

  return (
    <section id="pricing" className="py-20 md:py-32 bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="text-center mb-16"
        >
          <motion.span
            variants={fadeInUp}
            className="inline-block px-4 py-2 bg-primary/10 rounded-full text-sm font-medium text-primary mb-4"
          >
            {s.pricing_badge}
          </motion.span>
          <motion.h2
            variants={fadeInUp}
            className="text-3xl md:text-4xl lg:text-5xl text-text-primary mb-4"
            style={displayFont}
          >
            {s.pricing_h2_pre}<span className="text-primary">{s.pricing_h2_highlight}</span>
          </motion.h2>
          <motion.p
            variants={fadeInUp}
            className="text-base md:text-lg text-text-secondary max-w-2xl mx-auto"
          >
            {isAnniversary ? s.pricing_body_anniversary : s.pricing_body_wedding}
          </motion.p>
        </motion.div>

        {/* Unified pricing card */}
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="max-w-md mx-auto mb-6"
        >
          <div className="relative rounded-2xl p-8 bg-background-card border-2 border-primary flex flex-col">
            {plan === 'oneoff' && (
              <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                <div className="inline-flex items-center gap-1 px-3 py-1 bg-primary text-text-inverse text-xs font-medium rounded-full">
                  <Sparkles size={12} />
                  {s.pricing_most_popular}
                </div>
              </div>
            )}

            {/* Plan toggle pills */}
            <div className="flex gap-1 bg-background rounded-xl p-1 mb-6">
              <button
                type="button"
                onClick={() => setPlan('oneoff')}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                  plan === 'oneoff'
                    ? 'bg-background-card text-text-primary shadow-sm'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                {s.pricing_one_off}
              </button>
              <button
                type="button"
                onClick={() => setPlan('planner')}
                className={`flex-1 py-2 px-3 rounded-lg text-sm font-medium transition-all ${
                  plan === 'planner'
                    ? 'bg-background-card text-text-primary shadow-sm'
                    : 'text-text-secondary hover:text-text-primary'
                }`}
              >
                {s.pricing_planner_heading}
              </button>
            </div>

            {/* Currency selector */}
            <div className="flex justify-start mb-4">
              <SmallSelect
                value={currencyCode}
                onChange={setCurrencyCode}
                options={CURRENCY_SELECT_OPTIONS}
              />
            </div>

            {plan === 'oneoff' ? (
              <>
                <div className="mb-6 text-center">
                  <div className="flex items-baseline gap-2 justify-center">
                    <span className="text-3xl sm:text-4xl md:text-5xl font-bold text-text-primary">
                      {currency.symbol}{perInvitePrice}
                    </span>
                  </div>
                  <div className="flex justify-center mt-2">
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium bg-success/10 text-success px-3 py-1 rounded-full">
                      <Check className="w-3 h-3" strokeWidth={2.5} />
                      {s.pricing_one_time_payment}
                    </span>
                  </div>
                  {currencyCode !== 'GBP' && (
                    <p className="text-xs text-text-tertiary mt-2">
                      Indicative price · Stripe converts £{gbpBasePrice} to {currencyCode} at the live rate
                    </p>
                  )}
                </div>

                <ul className="space-y-3 mb-6 flex-1">
                  {features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <Check className="w-5 h-5 flex-shrink-0 text-success" />
                      <span className="text-text-primary">{feature}</span>
                    </li>
                  ))}
                </ul>

                {isAnniversary && (
                  <div className="mb-6 pt-4 border-t border-border">
                    <p className="text-xs text-text-tertiary mb-2">Not included (wedding invite features):</p>
                    <ul className="space-y-1.5">
                      {s.pricing_anniversary_not_included.map((f, i) => (
                        <li key={i} className="flex items-center gap-2 text-xs text-text-tertiary">
                          <span className="w-3.5 h-3.5 flex-shrink-0 text-center leading-none">–</span>
                          {f}
                        </li>
                      ))}
                    </ul>
                    <p className="text-xs text-text-tertiary mt-3">
                      Planning a wedding?{' '}
                      <Link to="/digital-wedding-invitations" className="text-primary hover:underline">
                        {s.pricing_anniversary_wedding_link}
                      </Link>
                    </p>
                  </div>
                )}

                <Link to="/create" className="btn btn-primary w-full text-center">
                  {s.pricing_cta}
                </Link>
              </>
            ) : (
              <>
                <div className="mb-6 text-center">
                  <p className="text-sm text-text-secondary mb-3">{s.pricing_planner_subheading}</p>
                  <div className="flex items-baseline gap-1 justify-center">
                    <span className="text-3xl sm:text-4xl md:text-5xl font-bold text-text-primary">
                      {currency.symbol}{currency.plannerYearly}
                    </span>
                    <span className="text-text-secondary text-lg">/yr</span>
                  </div>
                  {currencyCode !== 'GBP' && (
                    <p className="text-xs text-text-tertiary mt-2">
                      Indicative price · Stripe converts £199 to {currencyCode} at the live rate
                    </p>
                  )}
                </div>

                <ul className="space-y-3 mb-6 flex-1">
                  {s.pricing_planner_features.map((feature, i) => (
                    <li key={i} className="flex items-center gap-3">
                      <Check className="w-5 h-5 flex-shrink-0 text-success" />
                      <span className="text-text-primary">{feature}</span>
                    </li>
                  ))}
                </ul>

                <Link to="/create" className="btn btn-primary w-full text-center">
                  {s.pricing_planner_cta}
                </Link>
              </>
            )}
          </div>
        </motion.div>

        {/* Bespoke contact line */}
        <motion.p
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="text-center text-sm text-text-secondary mt-8"
        >
          {s.pricing_bespoke_pre}{' '}
          <a href="mailto:hello@wedsy.co?subject=Bespoke%20Invite%20Inquiry" className="text-primary hover:underline">
            {s.pricing_bespoke_link}
          </a>
        </motion.p>
      </div>
    </section>
  );
}
